import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm.admin;
const USAGES = ["waste", "meals", "both"];
const KINDS = ["product", "stock"];
const RAW_CATEGORY = "مواد خام";

/**
 * Create / edit one catalogue item. The unit is LOCKED once any record points
 * at the item (server: WM_ITEM_HAS_RECORDS) — we say so in the form instead of
 * letting the admin discover it through a 409.
 *
 * 2026-10-16 (D2): «النوع» — a product (costed from its POS recipe, linked in
 * «ربط الوصفات») or a raw ingredient taken from the stock count, which carries
 * the stock-count code (its cost) and the recipe ingredient key (so the daily
 * count can take raw waste off the theoretical use without a name match).
 */
export function ItemFormDialog({ item, units, onClose, onSaved }) {
    const { t, lang } = useI18n();
    const isNew = !item?.id;
    const [form, setForm] = useState({
        name_ar: item?.name_ar || "",
        name_en: item?.name_en || "",
        category: item?.category || "",
        usage: item?.usage || "both",
        unit_key: item?.unit_key || units[0]?.key || "piece",
        is_active: item?.is_active !== false,
        kind: item?.kind === "stock" ? "stock" : "product",
        sc_item_code: item?.sc_item_code || "",
        ingredient_key: item?.ingredient_key || "",
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const unitLocked = !isNew && (item?.records_count || 0) > 0;
    const stock = form.kind === "stock";

    // A new raw item is a waste item in «مواد خام» — the shape the seed gives the 11 D2 items.
    const setKind = (v) => setForm((f) => ({
        ...f, kind: v,
        ...(isNew && v === "stock" ? { usage: "waste", category: f.category || RAW_CATEGORY } : {}),
    }));

    const payload = () => {
        const base = {
            name_ar: form.name_ar, name_en: form.name_en, category: form.category, usage: form.usage,
            unit_key: form.unit_key, is_active: form.is_active, kind: form.kind,
        };
        if (!stock) return base;
        const code = form.sc_item_code.trim();
        const key = form.ingredient_key.trim();
        if (isNew) return { ...base, sc_item_code: code || null, ingredient_key: key || null };
        // PATCH: send what changed; "" (not null, which a patch reads as "unchanged") clears it
        const out = { ...base };
        if (code !== (item?.sc_item_code || "")) out.sc_item_code = code;
        if (key !== (item?.ingredient_key || "")) out.ingredient_key = key;
        return out;
    };

    const save = async () => {
        if (!form.name_ar.trim()) {
            toast.error(t("wm_admin_name_required"));
            return;
        }
        // a raw item IS its stock-count code (server: WM_ITEM_KIND_INVALID) — on
        // an edit too, whether the kind was just switched or the code cleared
        if (stock && !form.sc_item_code.trim()) {
            toast.error(t("wm_admin_sc_code_required"));
            return;
        }
        setBusy(true);
        try {
            if (isNew) {
                await api.post("/waste-meals/items", payload());
            } else {
                await api.patch(`/waste-meals/items/${item.id}`, payload());
            }
            toast.success(t("wm_saved"));
            await onSaved();
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid={C.itemDialog} className="max-h-[92vh] max-w-md overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t(isNew ? "wm_admin_item_add" : "wm_admin_item_edit")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-bold">{t("wm_admin_name_ar")} *</Label>
                        <Input data-testid={C.itemNameAr} value={form.name_ar} dir="rtl"
                               onChange={(e) => set("name_ar", e.target.value)} className="mt-1 h-9" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold">{t("wm_admin_name_en")}</Label>
                        <Input data-testid={C.itemNameEn} value={form.name_en} dir="ltr"
                               onChange={(e) => set("name_en", e.target.value)} className="mt-1 h-9" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold">{t("wm_admin_kind")}</Label>
                        <Select value={form.kind} onValueChange={setKind}>
                            <SelectTrigger data-testid={C.itemKind} className="mt-1 h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {KINDS.map((k) => (
                                    <SelectItem key={k} value={k} data-testid={C.itemKindOption(k)}>
                                        {t(`wm_admin_kind_${k}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="mt-1 text-[10px] leading-relaxed text-[color:var(--ink-faint)]">
                            {t(stock ? "wm_admin_kind_stock_hint" : "wm_admin_kind_product_hint")}
                        </p>
                    </div>
                    {stock ? (
                        <div className="space-y-3 rounded-lg border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] p-3">
                            <div>
                                <Label className="text-xs font-bold">{t("wm_admin_sc_code")} *</Label>
                                <Input data-testid={C.itemScCode} value={form.sc_item_code} dir="ltr"
                                       autoComplete="off" spellCheck={false} placeholder="PT-12200016"
                                       onChange={(e) => set("sc_item_code", e.target.value.toUpperCase())}
                                       className="mt-1 h-9 font-mono text-sm" />
                                <p className="mt-1 text-[10px] leading-relaxed text-[color:var(--ink-faint)]">
                                    {t("wm_admin_sc_code_hint")}
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs font-bold">{t("wm_admin_ingredient_key")}</Label>
                                <Input data-testid={C.itemIngredientKey} value={form.ingredient_key} dir="auto"
                                       autoComplete="off" spellCheck={false}
                                       onChange={(e) => set("ingredient_key", e.target.value)} className="mt-1 h-9" />
                                <p className="mt-1 text-[10px] leading-relaxed text-[color:var(--ink-faint)]">
                                    {t("wm_admin_ingredient_key_hint")}
                                </p>
                            </div>
                        </div>
                    ) : null}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-bold">{t("wm_admin_usage")}</Label>
                            <Select value={form.usage} onValueChange={(v) => set("usage", v)}>
                                <SelectTrigger data-testid={C.itemUsage} className="mt-1 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {USAGES.map((u) => (
                                        <SelectItem key={u} value={u} data-testid={C.itemUsageOption(u)}>
                                            {t(`wm_admin_usage_${u}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-bold">{t("wm_admin_unit")}</Label>
                            <Select value={form.unit_key} onValueChange={(v) => set("unit_key", v)}
                                    disabled={unitLocked}>
                                <SelectTrigger data-testid={C.itemUnit} className="mt-1 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {units.filter((u) => u.is_active !== false).map((u) => (
                                        <SelectItem key={u.key} value={u.key} data-testid={C.itemUnitOption(u.key)}>
                                            {lang === "ar" ? u.name_ar : u.name_en || u.name_ar}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {unitLocked ? (
                                <p data-testid={C.itemUnitLocked} className="mt-1 text-[10px] leading-relaxed text-[color:var(--warning-soft-fg)]">
                                    {t("wm_admin_unit_locked")}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-bold">{t("wm_admin_category")}</Label>
                        <Input data-testid={C.itemCategory} value={form.category}
                               onChange={(e) => set("category", e.target.value)} className="mt-1 h-9" />
                    </div>
                    <label className="flex items-center justify-between rounded-lg border border-[color:var(--border-soft)] px-3 py-2">
                        <span className="text-xs font-bold">{t("wm_admin_active")}</span>
                        <Switch data-testid={C.itemActive} checked={form.is_active}
                                onCheckedChange={(v) => set("is_active", v)} />
                    </label>
                    <p className="text-[10px] leading-relaxed text-[color:var(--ink-faint)]">
                        {t("wm_admin_inactive_hint")}
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid={C.itemCancel}>{t("cancel")}</Button>
                    <Button onClick={save} disabled={busy} data-testid={C.itemSave}>{t("save")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
